import { WithdrawalStatus } from "@redotech/cockroachdb-codegen/tables/returns/withdrawal-status";
import { WithdrawalListItem } from "@redotech/merchant-sdk/rpc/schema/withdrawals/list-withdrawals";
import { SortDirection } from "@redotech/redo-views-common/table";
import { RedoBadgeColor } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  RedoTableBadgeCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { ColumnAlignment } from "@redotech/redo-web/table";
import { Column2 } from "@redotech/redo-web/table2";

export enum WithdrawalTableColumnKey {
  ORDER = "order",
  CUSTOMER = "customer",
  CREATED = "created",
  TYPE = "type",
  STATUS = "status",
}

export const WITHDRAWAL_STATUS_LABELS: Record<WithdrawalStatus, string> = {
  [WithdrawalStatus.Pending]: "Pending",
  [WithdrawalStatus.Completed]: "Completed",
  [WithdrawalStatus.RequiresMerchantAction]: "Needs action",
  [WithdrawalStatus.Failed]: "Failed",
};

export const WITHDRAWAL_STATUS_COLORS: Record<
  WithdrawalStatus,
  RedoBadgeColor
> = {
  [WithdrawalStatus.Pending]: "warning",
  [WithdrawalStatus.Completed]: "success",
  [WithdrawalStatus.RequiresMerchantAction]: "warning",
  [WithdrawalStatus.Failed]: "error",
};

function formatDate(date: Date, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZone: timezone || undefined,
  }).format(new Date(date));
}

export function getWithdrawalColumns(
  merchantTimezone: string,
): Column2<WithdrawalListItem>[] {
  return [
    {
      alignment: ColumnAlignment.LEFT,
      key: WithdrawalTableColumnKey.ORDER,
      title: "Order",
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell leading text={row.orderName ?? "—"} />
      ),
      width: 120,
    },
    {
      alignment: ColumnAlignment.LEFT,
      key: WithdrawalTableColumnKey.CUSTOMER,
      title: "Customer",
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell text={row.customerName ?? "—"} />
      ),
      width: 160,
    },
    // TODO(withdrawal): add a "# of items" column once we decide where the
    // count comes from (linked Return for type=Return rows, Order line items
    // for cancelations) and add it to the list-withdrawals RPC response.
    {
      alignment: ColumnAlignment.LEFT,
      key: WithdrawalTableColumnKey.CREATED,
      title: "Created",
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell text={formatDate(row.createdAt, merchantTimezone)} />
      ),
      sort: SortDirection.DESC,
      width: 160,
    },
    {
      alignment: ColumnAlignment.LEFT,
      key: WithdrawalTableColumnKey.TYPE,
      title: "Type",
      renderNormalCell: ({ row }) => (
        <RedoTableBadgeCell
          badges={[
            row.returnId
              ? { text: "Return", color: "blueLight" }
              : { text: "Cancelation", color: "pink" },
          ]}
        />
      ),
      width: 120,
    },
    {
      alignment: ColumnAlignment.LEFT,
      key: WithdrawalTableColumnKey.STATUS,
      title: "Status",
      renderNormalCell: ({ row }) => (
        <RedoTableBadgeCell
          badges={[
            {
              text: WITHDRAWAL_STATUS_LABELS[row.status],
              color: WITHDRAWAL_STATUS_COLORS[row.status],
            },
          ]}
        />
      ),
      width: 140,
    },
  ];
}
